import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from './base';
import { Response } from '../../models/shared';
import { CustomerDTO, CustomerGroupDTO, CreateCustomerGroupDTO } from '../../models/customer';

const customerApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['customers', 'customer-groups'],
  endpoints: (builder) => ({
    getCustomers: builder.query<CustomerDTO[], { businessId: string }>({
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/customers`,
        method: 'GET',
      }),
      providesTags: ['customers'],
      transformResponse: (response: Response<CustomerDTO[]>) => response.data || [],
    }),
    editCustomer: builder.mutation<CustomerDTO, { businessId: string, data: Partial<CustomerDTO> }>({
      query: ({ businessId, data }) => ({
        url: `/businesses/${businessId}/customers/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['customers'],
    }),
    deleteCustomer: builder.mutation<CustomerDTO, { businessId: string, customerId: string }>({
      query: ({ businessId, customerId }) => ({
        url: `/businesses/${businessId}/customers/${customerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['customers'],
    }),
    getCustomerGroups: builder.query<CustomerGroupDTO[], { businessId: string }>({
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/customer-groups`,
        method: 'GET',
      }),
      providesTags: ['customer-groups'],
      transformResponse: (response: Response<CustomerGroupDTO[]>) => response.data || [],
    }),
    createCustomerGroup: builder.mutation<CustomerGroupDTO, { businessId: string, data: Partial<CreateCustomerGroupDTO> }>({
      query: ({ businessId, data }) => ({
        url: `/businesses/${businessId}/customer-groups`,
        method: 'POST',
        body: {
          name: data.name, business_id: businessId, description: '', is_active: true, event_ids: data.event_ids,
        },
      }),
      invalidatesTags: ['customer-groups', 'customers'],
    }),
    editCustomerGroup: builder.mutation<CustomerGroupDTO, { businessId: string, customerGroupId: string, data: Partial<CreateCustomerGroupDTO> }>({
      query: ({ businessId, customerGroupId, data }) => ({
        url: `/businesses/${businessId}/customer-groups`,
        method: 'PUT',
        body: {
          name: data.name, description: '', is_active: true, id: customerGroupId, event_ids: data.event_ids,
        },
      }),
      invalidatesTags: ['customer-groups', 'customers'],
    }),
    deleteCustomerGroup: builder.mutation<CustomerGroupDTO, { businessId: string, customerGroupId: string }>({
      query: ({ businessId, customerGroupId }) => ({
        url: `/businesses/${businessId}/customer-groups/${customerGroupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['customer-groups', 'customers'],
    }),
    exportCustomersXlsx: builder.mutation<void, { businessId: string }>({
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/customers/export-customers-xlsx`,
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `lista-clientes-${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}-${businessId}.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useEditCustomerMutation,
  useDeleteCustomerMutation,
  useGetCustomerGroupsQuery,
  useCreateCustomerGroupMutation,
  useEditCustomerGroupMutation,
  useDeleteCustomerGroupMutation,
  useExportCustomersXlsxMutation,
} = customerApi;
export { customerApi };
